// Two parts of the report: taps that pop in while an email goes out, and "Ready to book".
// Both count clicks only. Apple Mail opens every email by itself, so opens can't say who is interested.

import { useEffect, useRef, useState } from 'preact/hooks';
import { api } from '../lib/api';
import { Icon } from '../lib/icons';
import { linkClick } from '../lib/router';
import { ago, fullName, initials } from '../lib/format';

interface Tap {
  id: number;
  at: string;
  first_name: string | null;
  last_name: string | null;
  what: string;
}

export interface Lead {
  contact_id: number;
  first_name: string | null;
  last_name: string | null;
  email: string;
  phone: string | null;
  at: string;
  email_id: number;
  email_name: string;
}

/** Counts up to a number instead of jumping to it. Jumps straight there when motion is off. */
export function useCountUp(target: number, ms = 900): number {
  const [shown, setShown] = useState(target);
  const from = useRef(target);
  useEffect(() => {
    const start = from.current;
    if (start === target || matchMedia('(prefers-reduced-motion: reduce)').matches) {
      from.current = target;
      setShown(target);
      return;
    }
    const t0 = performance.now();
    let raf = 0;
    const step = (t: number) => {
      const k = Math.min(1, (t - t0) / ms);
      const v = Math.round(start + (target - start) * (1 - Math.pow(1 - k, 3)));
      from.current = v;
      setShown(v);
      if (k < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target]);
  return shown;
}

/** The newest taps, polled every few seconds while the email is live. */
export function LiveTaps({ id, live }: { id: number; live: boolean }) {
  const [taps, setTaps] = useState<Tap[]>([]);
  const last = useRef(0);
  useEffect(() => {
    let stop = false;
    const pull = async () => {
      try {
        const r = await api<{ taps: Tap[] }>(`/api/emails/${id}/live?after=${last.current}`);
        if (stop || !r.taps.length) return;
        last.current = r.taps[0].id;
        setTaps((old) => [...r.taps, ...old].slice(0, 5));
      } catch {
        // A missed poll is fine. The next one catches up.
      }
    };
    pull();
    if (!live) return () => (stop = true);
    const t = setInterval(pull, 4000);
    return () => {
      stop = true;
      clearInterval(t);
    };
  }, [id, live]);

  if (!taps.length) return null;
  return (
    <section class="card taps">
      <h2 class="card-title">
        {live && <span class="live-dot" aria-hidden="true" />}
        {live ? 'Tapping now' : 'Latest taps'}
      </h2>
      <ul class="tap-list" aria-live="polite">
        {taps.map((t) => (
          <li key={t.id} class="tap">
            <span class="avatar">{initials({ ...t, email: '?' })}</span>
            <span>
              <b>{fullName({ ...t, email: 'Someone' })}</b> tapped <b class={t.what === 'Book' ? 'tap-book' : ''}>{t.what}</b>
            </span>
            <small>{ago(t.at)}</small>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** People who tapped a booking link. One tap away from a call, a text or an email. */
export function ReadyToBook({ leads, title = 'Ready to book', showEmail = false, note, moreHref, limit = 5 }: { leads: Lead[]; title?: string; showEmail?: boolean; note?: string; moreHref?: string; limit?: number }) {
  const [all, setAll] = useState(false);
  if (!leads.length) return null;
  const list = all ? leads : leads.slice(0, limit);
  return (
    <section class="card book-card">
      <h2 class="card-title">
        <Icon name="calendar" size={18} /> {title} <i class="count">{leads.length}</i>
        {moreHref && (
          <a class="text-link book-more" href={moreHref} onClick={(e) => linkClick(e, moreHref)}>
            All in Contacts
          </a>
        )}
      </h2>
      {note && <p class="muted book-note">{note}</p>}
      <div class="leads">
        {list.map((l) => {
          const tel = l.phone?.replace(/[^\d+]/g, '');
          return (
            <div class="lead-row" key={l.contact_id}>
              <span class="avatar">{initials(l)}</span>
              <span class="lead-text">
                <b>{fullName(l)}</b>
                <small>
                  {ago(l.at)}
                  {showEmail && ` · ${l.email_name}`}
                </small>
              </span>
              <span class="lead-actions">
                {tel && (
                  <>
                    <a class="icon-btn" href={`tel:${tel}`} aria-label={`Call ${fullName(l)}`} title="Call">
                      <Icon name="call" size={18} />
                    </a>
                    <a class="icon-btn" href={`sms:${tel}`} aria-label={`Text ${fullName(l)}`} title="Text">
                      <Icon name="chat" size={18} />
                    </a>
                  </>
                )}
                <a class="icon-btn" href={`mailto:${l.email}`} aria-label={`Email ${fullName(l)}`} title="Email">
                  <Icon name="mail" size={18} />
                </a>
              </span>
            </div>
          );
        })}
      </div>
      {leads.length > limit && (
        <button class="link-btn" onClick={() => setAll(!all)}>
          {all ? 'Show fewer' : `Show all ${leads.length}`}
        </button>
      )}
    </section>
  );
}
